import { useEffect } from 'react';

const SCROLL_OVERLAY_SELECTOR = '.scroll-overlay';
const THUMB_SELECTOR = '[data-scroll-overlay-thumb="true"]';
const MIN_THUMB_HEIGHT = 24;
const THUMB_OFFSET_X = 6;

type OverlayState = {
  cleanup: () => void;
};

const getScrollOverlayElements = () =>
  Array.from(document.querySelectorAll<HTMLElement>(SCROLL_OVERLAY_SELECTOR));

const updateThumb = (element: HTMLElement, visible: boolean) => {
  const thumb = document.querySelector<HTMLElement>(
    `${THUMB_SELECTOR}[data-scroll-overlay-owner="${element.dataset.scrollOverlayId}"]`,
  );
  if (!thumb) return;

  const scrollableDistance = element.scrollHeight - element.clientHeight;
  const isScrollable = scrollableDistance > 1;
  const rect = element.getBoundingClientRect();
  const computedStyle = window.getComputedStyle(element);
  const paddingRight = Number.parseFloat(computedStyle.paddingRight) || 0;

  if (!isScrollable || rect.width <= 0 || rect.height <= 0) {
    thumb.style.opacity = '0';
    thumb.style.height = '0px';
    return;
  }

  const thumbHeight = Math.max(
    MIN_THUMB_HEIGHT,
    Math.round((element.clientHeight * element.clientHeight) / element.scrollHeight),
  );
  const maxThumbTop = Math.max(0, element.clientHeight - thumbHeight);
  const thumbTop =
    scrollableDistance > 0
      ? Math.round((element.scrollTop / scrollableDistance) * maxThumbTop)
      : 0;

  thumb.style.height = `${thumbHeight}px`;
  thumb.style.left = `${Math.round(rect.right - paddingRight + THUMB_OFFSET_X)}px`;
  thumb.style.top = `${Math.round(rect.top)}px`;
  thumb.style.opacity = visible ? '1' : '0';
  thumb.style.transform = `translate3d(0, ${thumbTop}px, 0)`;
};

const ensureOverlayThumb = (element: HTMLElement) => {
  if (!element.dataset.scrollOverlayId) {
    element.dataset.scrollOverlayId =
      window.crypto?.randomUUID?.() ?? `scroll-overlay-${Date.now()}-${Math.random()}`;
  }

  if (
    !document.querySelector(
      `${THUMB_SELECTOR}[data-scroll-overlay-owner="${element.dataset.scrollOverlayId}"]`,
    )
  ) {
    const thumb = document.createElement('div');
    thumb.dataset.scrollOverlayThumb = 'true';
    thumb.dataset.scrollOverlayOwner = element.dataset.scrollOverlayId;
    thumb.style.position = 'fixed';
    thumb.style.top = '0';
    thumb.style.left = '0';
    thumb.style.width = '2px';
    thumb.style.borderRadius = '2px';
    thumb.style.background = '#ffffff1a';
    thumb.style.opacity = '0';
    thumb.style.pointerEvents = 'none';
    thumb.style.zIndex = '1000';
    thumb.style.transition = 'opacity 160ms ease';
    thumb.style.willChange = 'height, transform, opacity';
    document.body.appendChild(thumb);
  }
};

const attachScrollOverlay = (element: HTMLElement): OverlayState => {
  ensureOverlayThumb(element);

  let hovering = false;
  let frame = 0;
  let resizeObserver: ResizeObserver | null = null;

  const scheduleUpdate = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      updateThumb(element, hovering);
    });
  };

  const handleMouseEnter = () => {
    hovering = true;
    scheduleUpdate();
  };

  const handleMouseLeave = () => {
    hovering = false;
    scheduleUpdate();
  };

  element.addEventListener('scroll', scheduleUpdate, { passive: true });
  element.addEventListener('mouseenter', handleMouseEnter);
  element.addEventListener('mouseleave', handleMouseLeave);
  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('scroll', scheduleUpdate, true);

  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(element);
  }

  scheduleUpdate();

  return {
    cleanup: () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      element.removeEventListener('scroll', scheduleUpdate);
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
      resizeObserver?.disconnect();
      document
        .querySelector(
          `${THUMB_SELECTOR}[data-scroll-overlay-owner="${element.dataset.scrollOverlayId}"]`,
        )
        ?.remove();
    },
  };
};

const ScrollOverlayRuntime = () => {
  useEffect(() => {
    const states = new WeakMap<HTMLElement, OverlayState>();

    const scan = () => {
      getScrollOverlayElements().forEach((element) => {
        if (!states.has(element)) {
          states.set(element, attachScrollOverlay(element));
        }
      });
    };

    scan();

    const observer = new MutationObserver(scan);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
      getScrollOverlayElements().forEach((element) => {
        states.get(element)?.cleanup();
      });
    };
  }, []);

  return null;
};

export default ScrollOverlayRuntime;
